import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CustomerGroupMenu from '../components/CustomerGroupMenu';
import Select from '../components/Select';
import SearchBox from '../components/SearchBox';
import Highlight from '../components/Highlight';
import { searchTerms, matchesAllTerms } from '../utils/search';
import {
  getCustomers,
  getCustomerTickets,
  assignCustomerGroup,
  updateCustomerGroup,
  deleteCustomerGroup,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime, fmtDateDMY } from '../utils/format';
import { canAction } from '../menu';
import { NtIcon, Pill, PageHeading, Th, NT_COLORS, STATUS_COLORS } from '../components/NotionUI';
import { customerColor, customerInitials } from '../utils/userColor';

const GROUP_COLORS = Object.values(NT_COLORS);

const initialsOf = customerInitials;
const avatarColor = customerColor;

function Customers({ user, onLogout }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canManage = canAction(user, 'customers.manage');
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all'); // 'all' | 'none' | id del gruppo
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // { id, name, color }
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});

  const load = async () => {
    try {
      const d = await getCustomers();
      setCustomers(d.customers || []);
      setGroups(d.groups || []);
      // un gruppo eliminato non può restare come filtro attivo
      setGroupFilter((f) => (typeof f === 'number' && !(d.groups || []).some((g) => g.id === f) ? 'all' : f));
    } catch (err) {
      setError(errorMessage(err, t('customers.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTickets = async (c) => {
    if (openId === c.id) {
      setOpenId(null);
      return;
    }
    setOpenId(c.id);
    if (details[c.id]?.tickets) return;
    setDetails((d) => ({ ...d, [c.id]: { loading: true } }));
    try {
      const res = await getCustomerTickets(c.id);
      setDetails((d) => ({ ...d, [c.id]: { tickets: res.tickets || [] } }));
    } catch (err) {
      setDetails((d) => ({ ...d, [c.id]: { error: errorMessage(err, t('customers.ticketsLoadError')) } }));
    }
  };

  // Assegna i clienti a un gruppo (esistente, nuovo) o li toglie dal gruppo. Rilancia l'errore per tenere aperto il menu.
  const assign = async (ids, payload) => {
    setError('');
    try {
      await assignCustomerGroup({ customer_ids: ids, ...payload });
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(errorMessage(err, t('customers.groupSaveError')));
      throw err;
    }
  };

  const saveGroup = async () => {
    setError('');
    try {
      await updateCustomerGroup(editing.id, { name: editing.name, color: editing.color });
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('customers.groupSaveError')));
    }
  };

  const removeGroup = async () => {
    const group = groups.find((g) => g.id === editing.id);
    if (!window.confirm(t('customers.groupDeleteConfirm', { name: group?.name || '' }))) return;
    setError('');
    try {
      await deleteCustomerGroup(editing.id);
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('customers.groupSaveError')));
    }
  };

  // Più parole separate da spazio: devono comparire tutte (AND), anche in campi diversi (nome, email, gruppo).
  const terms = useMemo(() => searchTerms(search), [search]);
  const visible = useMemo(() => customers.filter((c) => {
    if (filter === 'registered' && !c.registered) return false;
    if (filter === 'unregistered' && c.registered) return false;
    if (groupFilter === 'none' && c.group_id) return false;
    if (typeof groupFilter === 'number' && c.group_id !== groupFilter) return false;
    // Si cerca solo nel testo che si vede nelle colonne (nome, email, gruppo, tipo), non nelle date.
    return matchesAllTerms([
      c.name,
      c.email,
      c.group_name || '',
      t(c.registered ? 'customers.registered' : 'customers.unregistered'),
    ].join(' '), terms);
  }), [customers, terms, filter, groupFilter, t]);

  const stats = useMemo(() => ({
    total: customers.length,
    registered: customers.filter((c) => c.registered).length,
    withOpen: customers.filter((c) => c.tickets_open > 0).length,
    ungrouped: customers.filter((c) => !c.group_id).length,
  }), [customers]);

  const activeGroup = typeof groupFilter === 'number' ? groups.find((g) => g.id === groupFilter) : null;
  const selectedIds = visible.filter((c) => selected.has(c.id)).map((c) => c.id);
  const selectedRows = visible.filter((c) => selected.has(c.id));
  const allVisibleSelected = visible.length > 0 && selectedIds.length === visible.length;
  const commonGroup = selectedRows.length > 0 && selectedRows.every((c) => c.group_id === selectedRows[0].group_id)
    ? selectedRows[0].group_id
    : null;

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((c) => c.id)));

  const showGroupCol = canManage || groups.length > 0;
  const columns = 5 + (canManage ? 1 : 0) + (showGroupCol ? 1 : 0);

  const renderDetail = (c) => {
    const d = details[c.id];
    if (!d || d.loading) return <div className="loading">{t('common.loading')}</div>;
    if (d.error) return <div className="error-message">{d.error}</div>;
    return (
      <table className="nt-table customer-tickets-table">
        <thead>
          <tr>
            <Th icon="hash" />
            <Th icon="text">{t('customers.ticketSubject')}</Th>
            <Th icon="tag">{t('customers.ticketProject')}</Th>
            <Th icon="status">{t('customers.ticketStatus')}</Th>
            <Th icon="calendar">{t('customers.ticketCreated')}</Th>
          </tr>
        </thead>
        <tbody>
          {d.tickets.map((tk) => (
            <tr key={tk.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${tk.id}`, { state: { from: '/customers' } })}>
              <td className="nt-cell-id">{tk.id}</td>
              <td className="nt-cell-title">{tk.subject}</td>
              <td>
                {tk.project_name && (
                  <Pill color={tk.project_color}>{tk.project_name}</Pill>
                )}
              </td>
              <td>
                <Pill dot color={STATUS_COLORS[tk.status]}>{t(`tickets.status_${tk.status}`)}</Pill>
              </td>
              <td>{fmtDateDMY(tk.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const createIn = (ids) => (name) => assign(ids, { new_group_name: name });

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="user" title={t('customers.title')} subtitle={t('customers.subtitle')} />

        {error && <div className="error-message">{error}</div>}

        {!loading && customers.length > 0 && (
          <div className="cu-stats">
            <div className="cu-stat">
              <span className="cu-stat-icon"><NtIcon name="users" size={18} /></span>
              <span className="cu-stat-value">{stats.total}</span>
              <span className="cu-stat-label">{t('customers.statTotal')}</span>
            </div>
            <div className="cu-stat">
              <span className="cu-stat-icon cu-stat-green"><NtIcon name="user" size={18} /></span>
              <span className="cu-stat-value">{stats.registered}</span>
              <span className="cu-stat-label">{t('customers.statRegistered')}</span>
            </div>
            <div className="cu-stat">
              <span className="cu-stat-icon cu-stat-orange"><NtIcon name="ticket" size={18} /></span>
              <span className="cu-stat-value">{stats.withOpen}</span>
              <span className="cu-stat-label">{t('customers.statOpen')}</span>
            </div>
          </div>
        )}

        {!loading && customers.length > 0 && (groups.length > 0 || canManage) && (
          <section className="cg-panel">
            <div className="cg-panel-head">
              <h3>{t('customers.groupsTitle')}</h3>
              <p>{groups.length > 0 ? t('customers.groupsHint') : t('customers.groupsEmptyHint')}</p>
            </div>
            {groups.length > 0 && (
              <div className="cg-filter-row">
                <label className="nt-filter cg-select">
                  <NtIcon name="users" />
                  <span>{t('customers.groupFilter')}</span>
                  <Select
                    searchable
                    value={String(groupFilter)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setGroupFilter(v === 'all' || v === 'none' ? v : Number(v));
                      setEditing(null);
                    }}
                  >
                    <option value="all">{t('customers.groupAll')} ({stats.total})</option>
                    <option value="none">{t('customers.groupNoneFilter')} ({stats.ungrouped})</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id} data-color={g.color}>{g.name} ({g.customers_count})</option>
                    ))}
                  </Select>
                </label>
                {activeGroup && canManage && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm cg-edit-btn"
                    onClick={() => setEditing({ id: activeGroup.id, name: activeGroup.name, color: activeGroup.color })}
                  >
                    <NtIcon name="edit" size={13} /> {t('customers.groupEdit')}
                  </button>
                )}
              </div>
            )}

            {editing && (
              <form className="cg-editor" onSubmit={(e) => { e.preventDefault(); saveGroup(); }}>
                <input
                  value={editing.name}
                  maxLength={100}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  aria-label={t('customers.groupName')}
                  placeholder={t('customers.groupName')}
                  autoFocus
                />
                <div className="cg-swatches" role="radiogroup" aria-label={t('customers.groupColor')}>
                  {GROUP_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      role="radio"
                      aria-checked={editing.color === color}
                      className={'cg-swatch' + (editing.color === color ? ' cg-swatch-on' : '')}
                      style={{ background: color }}
                      onClick={() => setEditing({ ...editing, color })}
                      aria-label={color}
                    />
                  ))}
                </div>
                <span className="cg-editor-actions">
                  <button type="submit" className="btn btn-sm" disabled={!editing.name.trim()}>{t('common.save')}</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-secondary btn-sm cg-danger" onClick={removeGroup}>{t('customers.groupDelete')}</button>
                </span>
              </form>
            )}

            {activeGroup && (
              <div className="cg-summary">
                <span>
                  <strong>{activeGroup.name}</strong>
                  {' · '}{t('customers.groupEmails', { count: activeGroup.customers_count })}
                  {' · '}{t('customers.groupTickets', { total: activeGroup.tickets_total, open: activeGroup.tickets_open })}
                </span>
                {activeGroup.tickets_total > 0 && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/tickets?group=${activeGroup.id}`)}>
                    {t('customers.groupViewTickets')} →
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        <div className="cu-toolbar">
          <SearchBox className="cu-search" value={search} onChange={setSearch} placeholder={t('customers.search')} title={t('customers.searchHint')} />
          <div className="tpl-cats" role="group" aria-label={t('customers.type')}>
            {[
              ['all', t('customers.filterAll'), stats.total],
              ['registered', t('customers.filterRegistered'), stats.registered],
              ['unregistered', t('customers.filterUnregistered'), stats.total - stats.registered],
            ].map(([key, label, count]) => (
              <button key={key} type="button" className={'tpl-cat' + (filter === key ? ' tpl-cat-active' : '')} onClick={() => setFilter(key)}>
                {label} <span className="tpl-cat-count">{count}</span>
              </button>
            ))}
          </div>
          {!loading && <span className="nt-muted cu-count">{t('customers.count', { count: visible.length })}</span>}
        </div>

        {canManage && selectedIds.length > 0 && (
          <div className="cg-bulk" role="region" aria-label={t('customers.groupBulk')}>
            <strong>{selectedIds.length === 1 ? t('customers.groupSelectedOne') : t('customers.groupSelected', { count: selectedIds.length })}</strong>
            <CustomerGroupMenu
              className="btn btn-sm"
              groups={groups}
              currentGroupId={commonGroup}
              showRemove={selectedRows.some((c) => c.group_id)}
              onPick={(groupId) => assign(selectedIds, { group_id: groupId })}
              onCreate={createIn(selectedIds)}
            >
              <NtIcon name="tag" size={14} /> {t('customers.groupAssign')}
            </CustomerGroupMenu>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>{t('customers.groupClearSelection')}</button>
          </div>
        )}

        <div className="cu-card">
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : customers.length === 0 ? (
            <p className="nt-empty">{t('customers.noCustomers')}</p>
          ) : visible.length === 0 ? (
            <p className="nt-empty">{t('customers.noResults')}</p>
          ) : (
            <div className="nt-table-wrap">
              <table className="nt-table cu-table">
                <thead>
                  <tr>
                    {canManage && (
                      <th className="cg-check-col">
                        <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label={t('customers.selectAll')} />
                      </th>
                    )}
                    <Th icon="user">{t('customers.customer')}</Th>
                    {showGroupCol ? <Th icon="tag">{t('customers.groupColumn')}</Th> : null}
                    <Th icon="status">{t('customers.type')}</Th>
                    <Th icon="ticket">{t('customers.tickets')}</Th>
                    <Th icon="clock">{t('customers.lastTicket')}</Th>
                    <Th icon="calendar">{t('customers.customerSince')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr
                        className={(c.tickets_total > 0 ? 'cu-row-open-able' : '') + (selected.has(c.id) ? ' cg-row-selected' : '') || undefined}
                        onClick={() => c.tickets_total > 0 && toggleTickets(c)}
                      >
                        {canManage && (
                          <td className="cg-check-col" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleRow(c.id)} aria-label={t('customers.selectRow', { email: c.email })} />
                          </td>
                        )}
                        <td>
                          <div className="cu-person">
                            <span className="cu-avatar" style={{ background: avatarColor(c) }} aria-hidden="true">{initialsOf(c)}</span>
                            <span className="cu-person-text">
                              <span className="cu-name"><Highlight text={c.name} terms={terms} /></span>
                              <span className="cu-email"><Highlight text={c.email} terms={terms} /></span>
                            </span>
                          </div>
                        </td>
                        {showGroupCol ? (
                          <td>
                            {canManage ? (
                              <CustomerGroupMenu
                                className={c.group_id ? 'cg-trigger cg-trigger-set' : 'cg-trigger'}
                                title={t('customers.groupAssign')}
                                groups={groups}
                                currentGroupId={c.group_id}
                                onPick={(groupId) => assign([c.id], { group_id: groupId })}
                                onCreate={createIn([c.id])}
                              >
                                {c.group_id ? (
                                  <span className="cg-tag" style={{ '--pill': c.group_color }}><i className="cg-dot" aria-hidden="true" /><Highlight text={c.group_name} terms={terms} /></span>
                                ) : (
                                  <span className="cg-add"><NtIcon name="plus" size={12} /> {t('customers.groupAdd')}</span>
                                )}
                              </CustomerGroupMenu>
                            ) : c.group_id ? (
                              <span className="cg-tag" style={{ '--pill': c.group_color }}><i className="cg-dot" aria-hidden="true" /><Highlight text={c.group_name} terms={terms} /></span>
                            ) : (
                              <span className="nt-muted">—</span>
                            )}
                          </td>
                        ) : null}
                        <td>
                          <Pill dot color={c.registered ? NT_COLORS.green : NT_COLORS.grey}>
                            <Highlight text={t(c.registered ? 'customers.registered' : 'customers.unregistered')} terms={terms} />
                          </Pill>
                        </td>
                        <td>
                          {c.tickets_total > 0 ? (
                            <button
                              type="button"
                              className="cu-tickets-btn"
                              onClick={(e) => { e.stopPropagation(); toggleTickets(c); }}
                              aria-expanded={openId === c.id}
                              title={t('customers.showTickets')}
                            >
                              <span className="cu-chevron" aria-hidden="true">{openId === c.id ? '▾' : '▸'}</span>
                              {c.tickets_total}
                              {c.tickets_open > 0 && <span className="cu-open-badge">{t('customers.openCount', { count: c.tickets_open })}</span>}
                            </button>
                          ) : (
                            <span className="nt-muted">0</span>
                          )}
                        </td>
                        <td>{c.last_ticket_at ? fmtDateTime(c.last_ticket_at) : <span className="nt-muted">—</span>}</td>
                        <td>{fmtDateTime(c.created_at)}</td>
                      </tr>
                      {openId === c.id && (
                        <tr className="customer-tickets-row">
                          <td colSpan={columns}>
                            <div className="cu-detail">{renderDetail(c)}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Customers;
